import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ofType, createEffect, Actions } from '@ngrx/effects';
import { of, Observable } from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  tap,
  filter,
  withLatestFrom,
  take,
} from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TerminateConversation } from '@cigna/omni/conversations-state-data-access';

import { AuthService } from '@auth0/auth0-angular';

import {
  siteNavLoad,
  siteNavLoadSuccess,
  siteNavLoadError,
  headerNavLoad,
  headerNavLoadSuccess,
  headerNavLoadError,
  logout,
  loginStepup,
  loginStepupSuccess,
  loginStepupError,
  wamApprovalStatusLoad,
  wamApprovalStatusLoadSuccess,
  wamApprovalStatusLoadError,
} from './shell.actions';

import { NavigationService } from '../navigation/navigation.service';

import { Router, NavigationEnd } from '@angular/router';
import { RouterFacade } from '@cigna/shared/angular/core/router-state-data-access';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';

import { AppContext, NavigationItem, RouteData } from '@cigna/chcp/shared/util';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { WindowService } from '@cigna/shared/angular/core/window-util';
import {
  APP_CONTEXT,
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '@cigna/chcp/shared/environment-util';
import { CookieService } from 'ngx-cookie-service';
import { addDays } from 'date-fns';
import { UserFacade } from '@cigna/chcp/shared/user-profile-data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';
import { ChcpLaunchDarklyFlagKey } from '@cigna/chcp/shared/launch-darkly-util';

@Injectable()
export class ShellEffects {
  getAppNav$ = createEffect(() =>
    this.actions$.pipe(
      ofType(siteNavLoad),
      switchMap(() =>
        this.navigationService.getAppNav().pipe(
          map((data: NavigationItem[]) =>
            siteNavLoadSuccess({
              navItems: withLocalPrecertificationNav(
                data,
                this.envConfig.production,
              ),
            }),
          ),
          catchError((err) => of(siteNavLoadError({ error: err }))),
        ),
      ),
    ),
  );

  getHeaderNav$ = createEffect(() =>
    this.actions$.pipe(
      ofType(headerNavLoad),
      switchMap(() =>
        this.navigationService.getHeaderNav().pipe(
          map((data: NavigationItem[]) =>
            headerNavLoadSuccess({
              navItems: data,
            }),
          ),
          catchError((err) => of(headerNavLoadError({ error: err }))),
        ),
      ),
    ),
  );

  routeChange$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((e) => e instanceof NavigationEnd),
        withLatestFrom(
          this.routerFacade.data$ as Observable<RouteData>,
          this.routerFacade.url$,
        ),
        map(([, data, url]) => {
          const titlePrefix = this.appContext.toUpperCase();
          const title =
            data && data.title ? `${titlePrefix} - ${data.title}` : '';

          if (data && data.analytics && data.analytics.async !== true) {
            this._analytics.trackPageLoad(data, url);
          }
          if (title && title !== `${titlePrefix} - Login`) {
            this.titleService.setTitle(title);
          } else {
            // use a more SEO friendly title for login page
            this.titleService.setTitle(
              this.appContext === 'evernorth'
                ? 'Evernorth for Health Care Professionals'
                : 'Cigna for Health Care Professionals',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  processLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType('[Auth] Load Auth Success'),
        withLatestFrom(
          this.authFacade.userid$,
          this.authFacade.useridEncrypted$,
          this.authFacade.lob$,
          this.routerFacade.data$ as Observable<RouteData>,
          this.featureToggleFacade.featuresEnabled<ChcpLaunchDarklyFlagKey>([
            'ftr-chcp-global-team-web-ssoid-cja',
          ]),
        ),
        take(1),
        tap(
          ([
            ,
            userid,
            useridEncrypted,
            lob,
            routeData,
            isCjaPlainTextEnabled,
          ]) => {
            this.userFacade.updateLoginAudit();

            this._analytics.updateStore({
              user: {
                profile: {
                  ssoId: isCjaPlainTextEnabled ? userid : useridEncrypted,
                  ssoIdPlainText: userid,
                  lobType: lob,
                },
              },
            });
            if (routeData.routeCode !== 'resources') {
              this._analytics.trackLoginSuccess();
              this.cookieService.set('is-chcp-user', 'true', {
                secure: true,
                domain: 'cigna.com',
                expires: addDays(new Date(), 365),
              }); // requested by cigna.com team
            }
          },
        ),
      ),
    { dispatch: false },
  );

  loginStepup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginStepup),
      switchMap(() =>
        this.navigationService.loginStepUp().pipe(
          map((data: any) => loginStepupSuccess({ location: data.location })),
          catchError(() => of(loginStepupError())),
        ),
      ),
    ),
  );

  loginStepupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginStepupSuccess),
        tap(({ location }) => {
          if (location.includes('/app')) {
            // baseHref is /app so we need to trim off /app from location or it will go to /app/app/dasbhoard
            this.router.navigateByUrl(location.replace('/app', ''), {
              replaceUrl: true,
            });
          } else {
            this.windowService.location.href = location;
          }
        }),

        catchError((): any => {
          // todo: figure out what to do for error, should we redirect to login page maybe?
        }),
      ),
    { dispatch: false },
  );

  processTerminateConvo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      withLatestFrom(this.authFacade.userid$),
      map(
        ([{ variation }, ssoId]) =>
          new TerminateConversation(variation, '', ssoId || ''),
      ),
    ),
  );

  processLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        map(({ variation, linkName }) => {
          this._analytics.trackLogoutSuccess({ variation, linkName });

          sessionStorage.clear();

          if (this.cookieService.get('accessToken')) {
            this.cookieService.delete('accessToken', '/');
          }

          this.auth0Service.logout({
            logoutParams: {
              returnTo: `${
                this.envConfig.auth0.redirect_uri
              }/app/login?status=${
                variation === 'inactive' ? 'timeout' : 'logout'
              }`,
            },
          });
        }),
      ),
    { dispatch: false },
  );

  wamApprovalStatusLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(wamApprovalStatusLoad),
      switchMap(() =>
        this.navigationService.getWamApprovalStatus().pipe(
          map((status: string) => wamApprovalStatusLoadSuccess({ status })),
          catchError(() => of(wamApprovalStatusLoadError())),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private navigationService: NavigationService,
    private routerFacade: RouterFacade,
    private _analytics: ShellTrackHandlers,
    private titleService: Title,
    private authFacade: AuthFacade,
    private windowService: WindowService,
    private cookieService: CookieService,
    private auth0Service: AuthService,
    private userFacade: UserFacade,
    private featureToggleFacade: FeatureTogglesFacade,
    @Inject(APP_CONTEXT) private appContext: AppContext,
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {}
}

const LOCAL_PRECERTIFICATION_NAV: NavigationItem = {
  title: 'Precertification',
  path: '/precertification',
  openExternal: false,
  allowImpersonator: true,
  newTitle: false,
  identifier: 'chcp.precertification',
  hubkey: 'chcp.spa.app.precertification',
  children: [
    {
      title: 'Precertification',
      path: '/precertification/start',
      openExternal: false,
      allowImpersonator: true,
      newTitle: false,
      identifier: 'chcp.precertification.search',
      hubkey: 'chcp.spa.app.precertification.search',
    },
  ],
};

export function withLocalPrecertificationNav(
  navItems: NavigationItem[],
  isProduction: boolean | undefined,
): NavigationItem[] {
  if (isProduction !== false || !Array.isArray(navItems)) {
    return navItems;
  }

  const existingIndex = navItems.findIndex(
    (item) => item.identifier === 'chcp.precertification',
  );
  if (existingIndex !== -1) {
    return navItems.map((item, index) =>
      index === existingIndex ? pointPrecertificationChildAtStart(item) : item,
    );
  }

  const claimsIndex = navItems.findIndex(
    (item) => item.identifier === 'chcp.claim',
  );
  const insertAt = claimsIndex === -1 ? navItems.length : claimsIndex;

  return [
    ...navItems.slice(0, insertAt),
    LOCAL_PRECERTIFICATION_NAV,
    ...navItems.slice(insertAt),
  ];
}

function pointPrecertificationChildAtStart(
  item: NavigationItem,
): NavigationItem {
  return {
    ...item,
    children: item.children?.map((child) =>
      child.path === '/precertification/search'
        ? { ...child, path: '/precertification/start' }
        : child,
    ),
  };
}
